import s from './SchedulePage.module.css';
import AvatarComponent from '../../shared/ui/avatar/AvatarComponent';
import DropDownMenu from '../../shared/ui/dropdown-menu/DropDownMenu';
import { useEffect, useState } from 'react';
import { getRecentProjects } from '../../shared/api/textProjectApi';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/selectors/authSelectors';
import {
    selectRecentProjects,
    selectIsLoadingProject,
} from '../../store/selectors/projectSelectors';
import Spinner from '../../shared/ui/spinner/Spinner';
import { useAppDispatch } from '../../store/store';
import Button from '../../shared/ui/button/Button';
import ScheduleModal from './widgets/schedule-modal/ScheduleModal';
import UserProfileButton from '../../shared/ui/user-profile-button/UserProfileButton';
import { getShortTextFromHtml } from '../../shared/helpers/transformData';
import { socialMediaOptions } from '../../shared/constant/constant';
import IconLock from '../../shared/ui/icons/IconLock';
import { setPricingModal } from '../../store/slices/pageSlice';
import {
    selectIsBasicSubscription,
    selectIsFreeSubscription,
    selectPlan,
    selectPostsUsed,
} from '../../store/selectors/subscriptionSelector';

const MAX_SELECTED_PROJECTS = 5;
const SchedulePage = () => {
    const [selectedSocialMedia, setSelectedSocialMedia] = useState('all');
    const user = useSelector(selectUser);
    const projects = useSelector(selectRecentProjects);
    const isLoading = useSelector(selectIsLoadingProject);
    const dispatch = useAppDispatch();
    const isFreeSubscription = useSelector(selectIsFreeSubscription);
    const isBasicSubscription = useSelector(selectIsBasicSubscription);
    const postsUsed = useSelector(selectPostsUsed);

    const isRestrictScheduleFreeUser = postsUsed > 0 && isFreeSubscription;

    const isRestrictScheduleBasicUser = postsUsed > 15 && isBasicSubscription;

    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

    const socialMediaOptionsWithAllOption = [
        {
            value: 'all',
            disabled: false,
        },
        ...socialMediaOptions,
    ];
    useEffect(() => {
        if (user?.id && !isLoading) {
            dispatch(getRecentProjects(user.id));
        }
    }, [user?.id]);

    const filteredProjects =
        selectedSocialMedia === 'all'
            ? projects
            : projects.filter((project) => project.content_type === selectedSocialMedia);

    useEffect(() => {
        setSelectedProjects([]);
    }, [selectedSocialMedia]);

    const filteredSelectedProjects = selectedProjects.filter((projectId) =>
        filteredProjects.some((project) => project.id === projectId),
    );

    const handleProjectSelection = (projectId: string) => {
        setSelectedProjects((prev) => {
            if (prev.includes(projectId)) {
                return prev.filter((id) => id !== projectId);
            } else {
                if (prev.length < MAX_SELECTED_PROJECTS) {
                    return [...prev, projectId];
                }
                return prev;
            }
        });
    };

    const handleSelectAll = () => {
        if (filteredSelectedProjects.length > 0) {
            setSelectedProjects((prev) =>
                prev.filter(
                    (projectId) => !filteredProjects.some((project) => project.id === projectId),
                ),
            );
        } else {
            const projectsToSelect = filteredProjects.slice(0, MAX_SELECTED_PROJECTS);
            setSelectedProjects((prev) => [
                ...prev,
                ...projectsToSelect.map((project) => project.id),
            ]);
        }
    };

    const handleSchedule = () => {
        if (isRestrictScheduleFreeUser || isRestrictScheduleBasicUser) {
            dispatch(setPricingModal(true));
        } else {
            if (selectedProjects.length > 0) {
                setIsScheduleModalOpen(true);
            }
        }
    };

    const handleCloseScheduleModal = () => {
        setIsScheduleModalOpen(false);
    };
    return (
        <div className={s.container}>
            <div className={s.header}>
                <h1 className={s.header_title}>Schedule</h1>
                <UserProfileButton />
            </div>
            <div className={s.content}>
                <div className={s.top_content}>
                    <h3 className={s.top_content_title}>Select posts to schedule</h3>
                    <DropDownMenu
                        width={150}
                        options={socialMediaOptionsWithAllOption}
                        selectedOption={selectedSocialMedia}
                        setSelectedOption={(option) => setSelectedSocialMedia(option)}
                    />
                </div>
                {filteredSelectedProjects.length > 0 && (
                    <div className={s.selected_projects}>
                        <div className={s.selection_header}>
                            <div className={s.selection_info}>
                                <span className={s.selection_text}>
                                    {filteredSelectedProjects.length} selected on this page
                                </span>
                                <button className={s.select_all_btn} onClick={handleSelectAll}>
                                    {filteredSelectedProjects.length > 0
                                        ? 'Clear selection'
                                        : `Select ${Math.min(MAX_SELECTED_PROJECTS, filteredProjects.length)} posts`}
                                </button>
                            </div>
                            <Button
                                text="Schedule"
                                icon={
                                    isRestrictScheduleFreeUser || isRestrictScheduleBasicUser ? (
                                        <IconLock color="#fff" />
                                    ) : undefined
                                }
                                style={{
                                    width: '150px',
                                    background:
                                        '    linear-gradient(90deg, #8801FF 0%, #D400FF 100%)',
                                    color: 'white',
                                }}
                                onClick={handleSchedule}
                            />
                        </div>
                    </div>
                )}
                <div className={s.bottom_content}>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <div className={s.projects}>
                            {filteredProjects.length === 0 && (
                                <p className={s.no_projects}>No posts for {selectedSocialMedia}</p>
                            )}
                            {filteredProjects.map((project) => {
                                const isSelected = selectedProjects.includes(project.id);
                                const isDisabled =
                                    !isSelected &&
                                    filteredSelectedProjects.length >= MAX_SELECTED_PROJECTS;

                                return (
                                    <div
                                        key={project.id}
                                        className={`${s.project_container} ${isDisabled ? s.disabled : ''}`}
                                        onClick={() =>
                                            !isDisabled && handleProjectSelection(project.id)
                                        }
                                        style={{
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            position: 'relative',
                                            opacity: isDisabled ? 0.5 : 1,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() =>
                                                !isDisabled && handleProjectSelection(project.id)
                                            }
                                            className={s.project_checkbox}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isDisabled}
                                        />
                                        <div className={s.project}>
                                            {project.image?.url ? (
                                                <img
                                                    src={project.image.url}
                                                    alt={project.name}
                                                    className={s.project_image}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    draggable={false}
                                                />
                                            ) : (
                                                <div className={s.empty_image}>No Image</div>
                                            )}

                                            <div className={s.user_info}>
                                                <AvatarComponent
                                                    avatarUrl={user?.profile_picture ?? ''}
                                                    userName={
                                                        user?.name ||
                                                        user?.username ||
                                                        user?.email ||
                                                        ''
                                                    }
                                                    width={20}
                                                    height={20}
                                                />
                                                <p className={s.user_name}>
                                                    {user?.name ||
                                                        user?.username ||
                                                        user?.email ||
                                                        ''}
                                                </p>
                                            </div>
                                            <p className={s.project_name}>
                                                {getShortTextFromHtml(project.title || '')}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <ScheduleModal
                isOpen={isScheduleModalOpen}
                onClose={handleCloseScheduleModal}
                selectedProjects={selectedProjects}
            />
        </div>
    );
};

export default SchedulePage;
